import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCriticalThinkingItems } from '../lib/api';
import { useReducedMotion } from 'framer-motion';
import { motion, AnimatePresence, springs, PageTransition, Pressable, FadeIn, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

type Cat = { key: string; label: string };

const DISCIPLINE_META: Record<string, { title: string; subtitle: string; categories: Cat[] }> = {
  nursing: {
    title: 'Nursing',
    subtitle: 'Case studies, pharmacology rationales, and clinical stories',
    categories: [
      { key: '', label: 'All' },
      { key: 'case_study', label: 'Case Studies' },
      { key: 'pharmacology', label: 'Pharmacology' },
      { key: 'med_surg', label: 'Med Surg' },
      { key: 'icu', label: 'ICU' },
      { key: 'er', label: 'ER' },
    ],
  },
  medicine: {
    title: 'Medicine',
    subtitle: 'Clinical cases, diagnostic reasoning, pharmacology, and emergencies',
    categories: [
      { key: '', label: 'All' },
      { key: 'med_case', label: 'Clinical Cases' },
      { key: 'med_diagnosis', label: 'Diagnosis' },
      { key: 'med_pharm', label: 'Pharmacology' },
      { key: 'med_emergency', label: 'Emergencies' },
      { key: 'med_ethics', label: 'Ethics & EBM' },
    ],
  },
  law: {
    title: 'Law',
    subtitle: 'Case briefs, doctrine, issue-spotting, and exam prep',
    categories: [
      { key: '', label: 'All' },
      { key: 'law_case', label: 'Case Briefs' },
      { key: 'law_doctrine', label: 'Doctrine' },
      { key: 'law_issue', label: 'Issue Spotting' },
      { key: 'law_exam', label: 'Exam Prep' },
      { key: 'law_ethics', label: 'Ethics' },
    ],
  },
};

const CATEGORY_LABELS: Record<string, string> = {
  case_study: 'Case Study', pharmacology: 'Pharmacology', med_surg: 'Med Surg', icu: 'ICU', er: 'ER',
  med_case: 'Clinical Case', med_diagnosis: 'Diagnosis', med_pharm: 'Pharmacology', med_emergency: 'Emergency', med_ethics: 'Ethics / EBM',
  law_case: 'Case Brief', law_doctrine: 'Doctrine', law_issue: 'Issue Spotting', law_exam: 'Exam', law_ethics: 'Ethics',
};

const CATEGORY_COLORS: Record<string, string> = {
  case_study: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  pharmacology: 'bg-purple-100 text-purple-700 border-purple-200',
  med_surg: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 border-emerald-200',
  icu: 'bg-red-100 text-red-700 border-red-200',
  er: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200',
  med_case: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  med_diagnosis: 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-200',
  med_pharm: 'bg-purple-100 text-purple-700 border-purple-200',
  med_emergency: 'bg-red-100 text-red-700 border-red-200',
  med_ethics: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  law_case: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 border-amber-200',
  law_doctrine: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200',
  law_issue: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 border-emerald-200',
  law_exam: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 border-blue-200',
  law_ethics: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

/** Small tinted marker used in section headers (calmer than icons). */
const CATEGORY_DOTS: Record<string, string> = {
  case_study: 'bg-blue-400', pharmacology: 'bg-purple-400', med_surg: 'bg-emerald-400', icu: 'bg-red-400', er: 'bg-orange-400',
  med_case: 'bg-blue-400', med_diagnosis: 'bg-cyan-400', med_pharm: 'bg-purple-400', med_emergency: 'bg-red-400', med_ethics: 'bg-indigo-400',
  law_case: 'bg-amber-400', law_doctrine: 'bg-orange-400', law_issue: 'bg-emerald-400', law_exam: 'bg-blue-400', law_ethics: 'bg-indigo-400',
};

function difficultyLabel(d: number) {
  if (d <= 2) return 'Beginner';
  if (d <= 3) return 'Intermediate';
  if (d <= 4) return 'Advanced';
  return 'Expert';
}
function difficultyColor(d: number) {
  if (d <= 2) return 'text-emerald-600';
  if (d <= 3) return 'text-amber-600';
  if (d <= 4) return 'text-orange-600';
  return 'text-red-600';
}

export function CriticalThinkingPage() {
  const navigate = useNavigate();
  const { discipline = 'nursing' } = useParams();
  const meta = (DISCIPLINE_META[discipline] ?? DISCIPLINE_META.nursing)!;
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCriticalThinkingItems(discipline, category || undefined);

  useEffect(() => { window.scrollTo(0, 0); setCategory(''); setSearch(''); }, [discipline]);

  const items = data?.items ?? [];

  // Live filter: kicks in once the user types 2+ characters. Matches the visible
  // text of each item (title, topic, clinical setting, category label).
  const q = search.trim().toLowerCase();
  const searchActive = q.length >= 2;
  const visibleItems = useMemo(() => {
    if (!searchActive) return items;
    return items.filter((it: any) =>
      [it.title, it.topic, it.clinicalSetting, CATEGORY_LABELS[it.category] ?? it.category]
        .some((f) => typeof f === 'string' && f.toLowerCase().includes(q)),
    );
  }, [items, q, searchActive]);

  const grouped = useMemo(() => {
    if (category) return null;
    const groups: Record<string, typeof items> = {};
    for (const item of visibleItems) (groups[item.category] ??= []).push(item);
    return groups;
  }, [visibleItems, category]);

  return (
    <PageTransition className="max-w-2xl mx-auto pb-20 space-y-4">
      <Pressable
        onClick={() => navigate('/learn')}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-squishy-primary transition-colors min-h-[44px] -ml-1 px-1"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        Learn
      </Pressable>

      <FadeIn>
        <h1 className="font-display text-2xl font-semibold text-squishy-dark">{meta.title} Critical Thinking</h1>
        <p className="text-sm text-slate-500 mt-1">{meta.subtitle}</p>
      </FadeIn>

      {/* Category filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
        {meta.categories.map((c) => {
          const active = category === c.key;
          return (
            <button
              key={c.key}
              onClick={() => { haptic.selection(); setCategory(c.key); }}
              aria-pressed={active}
              className={`relative shrink-0 min-h-[44px] px-4 rounded-full text-xs font-semibold transition-colors ${
                active ? 'text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              {active && (
                <motion.span
                  layoutId="ct-filter-pill"
                  className="absolute inset-0 rounded-full bg-squishy-dark shadow-sm"
                  transition={springs.snappy}
                  aria-hidden
                />
              )}
              <span className="relative">{c.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.4a6.75 6.75 0 11-13.5 0 6.75 6.75 0 0113.5 0z" />
        </svg>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${meta.title} cases...`}
          className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-11 py-3 text-sm text-squishy-dark placeholder-slate-400 shadow-card focus:outline-none focus:border-squishy-primary focus:shadow-glow transition-shadow"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 text-lg leading-none"
          >×</button>
        )}
      </div>

      {/* Results */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={isLoading ? 'loading' : `${category}|${searchActive ? 'search' : 'browse'}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="space-y-4"
        >
          {isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-[84px] rounded-2xl" />
              ))}
            </div>
          ) : searchActive && visibleItems.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">No results for "{search.trim()}".</p>
          ) : category ? (
            <div className="space-y-2.5">
              {visibleItems.map((item) => (
                <ItemCard key={item.id} item={item} onClick={() => navigate(`/learn/${discipline}/${item.externalId}`)} />
              ))}
              {visibleItems.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No items in this category yet.</p>}
            </div>
          ) : (
            Object.entries(grouped ?? {}).map(([cat, catItems], gi) => (
              <FadeIn key={cat} delay={Math.min(gi * 0.05, 0.25)} y={10}>
                <div className="flex items-baseline gap-2 mb-2.5 mt-1">
                  <span className={`w-2 h-2 rounded-full self-center ${CATEGORY_DOTS[cat] ?? 'bg-slate-300'}`} aria-hidden />
                  <h2 className="font-display text-base font-semibold text-squishy-dark">{CATEGORY_LABELS[cat] ?? cat.replace(/_/g, ' ')}</h2>
                  <span className="text-[11px] text-slate-400 font-medium">{catItems.length} items</span>
                </div>
                <div className="space-y-2.5">
                  {catItems.map((item) => (
                    <ItemCard key={item.id} item={item} onClick={() => navigate(`/learn/${discipline}/${item.externalId}`)} />
                  ))}
                </div>
              </FadeIn>
            ))
          )}
        </motion.div>
      </AnimatePresence>
    </PageTransition>
  );
}

function ItemCard({ item, onClick }: { item: any; onClick: () => void }) {
  const reduce = useReducedMotion();
  const colorClass = CATEGORY_COLORS[item.category] ?? 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <Pressable
      onClick={onClick}
      whileHover={reduce ? undefined : { y: -2 }}
      className="group w-full text-left bg-white border border-slate-200/80 rounded-2xl px-4 py-3.5 shadow-card hover:shadow-pop hover:border-squishy-primary/60 transition-[box-shadow,border-color]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold text-squishy-dark leading-snug group-hover:text-squishy-primary transition-colors">{item.title}</h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.topic}</p>
        </div>
        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colorClass}`}>
          {CATEGORY_LABELS[item.category] ?? item.category.replace(/_/g, ' ')}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-2.5">
        <span className={`text-[11px] font-semibold ${difficultyColor(item.difficulty)}`}>{difficultyLabel(item.difficulty)}</span>
        {item.clinicalSetting && <span className="text-[11px] text-slate-400 truncate max-w-[160px]">{item.clinicalSetting}</span>}
        <div className="flex gap-1 ml-auto items-center" aria-label={`Difficulty ${item.difficulty} of 5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i < item.difficulty ? 'bg-squishy-primary' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>
    </Pressable>
  );
}
